import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../translations';
import CameraScanner from '../components/scanner/CameraScanner';
import { phoneHello, phoneSendCode } from '../components/scanner/scanApi';
import { CheckIcon, AlertIcon, PhoneIcon } from '../ui/icons';

// Strona otwierana NA TELEFONIE po zeskanowaniu QR z komputera (`/scan/:token`).
//
// Publiczna, bez logowania: token w adresie pozwala wyłącznie wysłać kod do jednego okna na
// komputerze (patrz Login/backend/lib/scanSessions.js). Strona nie pokazuje żadnych danych firmy
// — nie ma ich skąd wziąć i nie powinna ich mieć.

const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  box-sizing: border-box;
  padding: calc(16px + var(--safe-top, 0px)) 16px calc(24px + var(--safe-bottom, 0px));
  background: var(--bg);
  color: var(--text);
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 560px;
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;

  h1 { margin: 0; font-size: var(--fs-lg); }
  p { margin: 2px 0 0; font-size: var(--fs-sm); color: var(--text-muted); }
`;

const Notice = styled.div`
  padding: 14px 16px;
  border-radius: var(--r-md);
  font-size: var(--fs-sm);
  line-height: 1.5;
  background: ${(p) => (p.$tone === 'error' ? 'var(--danger-bg)' : 'var(--info-bg)')};
  border: 1px solid ${(p) => (p.$tone === 'error' ? 'var(--danger-border)' : 'var(--info-border)')};
`;

const Sent = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 10px 12px;
    border-radius: var(--r-md);
    background: var(--card-bg);
    border: 1px solid var(--input-border);
    font-size: var(--fs-sm);
  }
  li[data-tone='ok'] svg { color: var(--success); }
  li[data-tone='warn'] svg, li[data-tone='error'] svg { color: var(--warning); }
  strong { font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  span { color: var(--text-muted); }
`;

export default function PhoneScanner() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [state, setState] = useState('checking'); // checking | ready | expired
  const [sent, setSent] = useState([]);

  useEffect(() => {
    let cancelled = false;
    phoneHello(token)
      .then(() => { if (!cancelled) setState('ready'); })
      .catch(() => { if (!cancelled) setState('expired'); });
    return () => { cancelled = true; };
  }, [token]);

  // Ekran nie gaśnie w trakcie skanowania serii sprzętu — przy ladzie telefon leży z aparatem
  // w górę, a zgaszony ekran zrywa kamerę. Best-effort: nie każda przeglądarka ma Wake Lock.
  useEffect(() => {
    if (state !== 'ready' || !navigator.wakeLock) return undefined;
    let lock = null;
    const acquire = () => navigator.wakeLock.request('screen').then((l) => { lock = l; }).catch(() => {});
    acquire();
    const onVisible = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (lock) lock.release().catch(() => {});
    };
  }, [state]);

  const onCode = useCallback(async (code) => {
    let wynik;
    try {
      const r = await phoneSendCode(token, code);
      wynik = r.delivered > 0
        ? { tone: 'ok', text: t('phoneScanner.sent').replace('{code}', code) }
        : { tone: 'warn', text: t('phoneScanner.noListener') };
    } catch (e) {
      const status = e && e.response && e.response.status;
      if (status === 404) setState('expired');
      wynik = {
        tone: 'error',
        text: status === 404 ? t('phoneScanner.expiredShort')
          : status === 429 ? t('phoneScanner.tooFast')
            : t('phoneScanner.sendFailed'),
      };
    }
    setSent((prev) => [{ code, ...wynik, at: Date.now() }, ...prev].slice(0, 10));
    return wynik;
  }, [token, t]);

  return (
    <Page data-cy="phone-scanner-page" data-state={state}>
      <Header>
        <PhoneIcon size={28} />
        <div>
          <h1>{t('phoneScanner.title')}</h1>
          <p>{t('phoneScanner.subtitle')}</p>
        </div>
      </Header>

      {state === 'checking' && <Notice>{t('phoneScanner.checking')}</Notice>}

      {state === 'expired' && (
        <Notice $tone="error" role="alert" data-cy="phone-scanner-expired">
          {t('phoneScanner.expired')}
        </Notice>
      )}

      {state === 'ready' && <CameraScanner onCode={onCode} tall naTelefonie />}

      {sent.length > 0 && (
        <Sent data-cy="phone-scanner-sent" aria-live="polite">
          {sent.map((s) => (
            <li key={s.at} data-tone={s.tone}>
              {s.tone === 'ok' ? <CheckIcon size={18} /> : <AlertIcon size={18} />}
              <div>
                <strong>{s.code}</strong>
                <br />
                <span>{s.text}</span>
              </div>
            </li>
          ))}
        </Sent>
      )}
    </Page>
  );
}
